import type { Msg } from "nats.ws";
import { headers, NatsError, nuid } from "nats.ws";

import { DisconnectedError } from "@/error/DisconnectedError";
import { useConnectionStore } from "@/store/connection";
import { useStatisticsStore } from "@/store/statistics";
import type { UnknownRecord } from "@/utils/type";

import { BackendError } from "../BackendError";
import type { Connection } from "../Connection";
import { TimeoutError } from "../TimeoutError";
import type { ServerAlmType } from "../serverModel";
import { BaseRpcBackend } from "./BaseRpcBackend";
import { NatsRPCPayload } from "./NatsRPCPayload";
import type { RpcBackend } from "./RpcBackend";
import { decodeJson } from "./jsonCodec";
import type { CallContext } from "./requestHelper";
import { createCallContext, createCallHeaders } from "./requestHelper";
import { handleResponse } from "./responseHelper";

export class NatsRpcBackend extends BaseRpcBackend<Msg> implements RpcBackend {
  private sessionId!: string;
  private almType?: ServerAlmType;

  constructor(
    private connection: Connection,
    private company: string,
    private userId: string,
  ) {
    super("nats");
  }

  init(sessionId: string, almType?: ServerAlmType) {
    this.sessionId = sessionId;
    this.almType = almType;
  }

  async doCall<T>(
    base: string,
    path: string,
    args: unknown[] = [],
    kwargs?: UnknownRecord,
    timeout?: number,
    retry = 0,
  ): Promise<T> {
    const context = createCallContext(base, path, args, kwargs, timeout, retry);
    const reconnects = useConnectionStore().reconnects;
    try {
      const res = await this.callBackend(context);
      useStatisticsStore().addOk("sends");
      return handleResponse(decodeJson(res.data), context) as T;
      // FIXME: Simply casting the response is not a good idea because we are
      // basically removing all advantages of Typescript on the calling
      // functions.
      //
      // To conform to the signature of BackendSession we are still casting here,
      // but this should be changed to a parsing strategy where the format is
      // validated before being converted.
    } catch (err) {
      useStatisticsStore().addFail("sends", err);
      if (err instanceof NatsError) {
        if (err.code === "TIMEOUT") {
          if (!useConnectionStore().isConnected) {
            throw new DisconnectedError();
          }
          if (useConnectionStore().reconnects !== reconnects && retry < 3) {
            return this.doCall(base, path, args, kwargs, timeout, retry + 1);
          }
          throw new TimeoutError({ request: context, response: connectionInfo() });
        }
        throw new BackendError(err.name, `Nats Error while calling RPC method ${base + path}`, {
          request: context,
          response: {
            ...connectionInfo(),
            message: err.message,
            code: err.code,
            cause: err.chainedError,
          },
        });
      }
      throw err;
    }

    function connectionInfo() {
      return { connections: useConnectionStore().wsStates() };
    }
  }

  async doCallBackend(context: CallContext) {
    const callHeaders = headers();
    for (const [key, value] of Object.entries(createCallHeaders(context))) {
      callHeaders.append(key, value);
    }
    const payload = new NatsRPCPayload({ args: context.args, kwargs: context.kwargs });
    return await this.connection.request(context.base + context.path, payload.encode(), {
      timeout: context.requestTimeout,
      reply: `_INBOX.${this.userId}.${context.path}.${nuid.next()}`,
      noMux: true,
      headers: callHeaders,
    });
  }

  async roomCall<T>(path: string, args: unknown[] = []) {
    return await this.doCall<T>(`piplanning.session.6.${this.company}.${this.userId}.`, path, args);
  }

  async adminCall<T>(path: string, args: unknown[] = []) {
    return await this.doCall<T>(`piplanning.admin.6.${this.company}.${this.userId}.`, path, args);
  }

  async almCall<T>(path: string, args: unknown[] = []) {
    const base = `piplanning.almsession.6.${this.company}.${this.userId}.`;
    return await this.doCall<T>(base, path, args);
  }

  async almToolCall<T>(path: string, args: unknown[], timeout?: number) {
    const base = `piplanning.almsession.6.${this.company}.${this.userId}.`;
    const almPath = `${this.almType || "none"}.${path}`;
    return await this.doCall<T>(base, almPath, args, undefined, timeout);
  }

  async companyCall<T>(path: string, args: unknown[] = []) {
    return await this.doCall<T>(`piplanning.company.3.${this.company}.${this.userId}.`, path, args);
  }

  async sessionCall<T>(path: string, args: unknown[] = [], kwargs?: UnknownRecord) {
    const base = `piplanning.session.6.${this.company}.${this.userId}.`;
    return await this.doCall<T>(base, path, [this.sessionId, ...args], kwargs);
  }

  async staticSessionCall<T>(path: string, args: unknown[] = [], kwargs?: UnknownRecord) {
    const base = `piplanning.session.6.${this.company}.${this.userId}.`;
    return await this.doCall<T>(base, path, args, kwargs);
  }

  async serverCall<T>(
    path: string,
    args: unknown[] = [],
    kwargs?: UnknownRecord,
    timeout?: number,
  ) {
    const base = `piplanning.server.6.${this.company}.${this.userId}.`;
    return await this.doCall<T>(base, path, args, kwargs, timeout);
  }

  async statusCall<T>(path: string, args: unknown[] = [], kwargs?: UnknownRecord) {
    const base = `piplanning.session.6.${this.company}.${this.userId}.`;
    return await this.doCall<T>(base, path, args, kwargs);
  }

  async boardCall<T>(path: string, args: unknown[], kwargs?: UnknownRecord) {
    const base = `piplanning.board.6.${this.company}.${this.userId}.`;
    return await this.doCall<T>(base, path, args, kwargs);
  }

  async stickyCall<T>(path: string, args: unknown[], kwargs?: UnknownRecord) {
    const base = `piplanning.sticky.6.${this.company}.${this.userId}.`;
    return await this.doCall<T>(base, path, args, kwargs);
  }

  async flexBoardCall<T>(path: string, args: unknown[] = [], kwargs?: UnknownRecord) {
    const base = `piplanning.flexboard.6.${this.company}.${this.userId}.`;
    return await this.doCall<T>(base, path, args, kwargs);
  }

  async userCall<T>(path: string) {
    return await this.doCall<T>(`piplanning.authuser.2.${this.company}.${this.userId}.`, path);
  }
}
